import {
    createUserWithEmailAndPassword,
    User as FirebaseUser,
    GoogleAuthProvider,
    onAuthStateChanged,
    sendPasswordResetEmail,
    signInAnonymously,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { auth, db } from '../config/firebase';
import { getApiBaseUrl } from '../utils/apiConfig';
// Keeps the profile object stable across snapshots that changed nothing, which
// is what stopped every dashboard re-subscribing on any write to the user doc.
import { sameProfile } from '../lib/auth/sameProfile';
import { claimsLookStale, ensureFreshClaims, resetClaimsRefresh } from '../lib/auth/claimsRefresh';
import { resolveSignupRole } from '../lib/auth/signupRole';
import { createDefaultSubscription } from '../services/subscriptionService';
import {
    ApprovalStatus,
    isGuestUser,
    normalizeUserRole,
    UserProfile,
    UserRole
} from '../utils/rbac';

export type ModalType = 'subscription' | 'upgrade' | null;

export interface ModalContextType {
  activeModal: ModalType;
  openModal: (modal: ModalType) => void;
  closeModal: () => void;
}

// Re-export types from rbac for convenience
export type { ApprovalStatus, UserProfile, UserRole };

/**
 * Ask the server to re-mirror this user's identity onto their token.
 *
 * Only worth doing when the token actually disagrees with the document — this used
 * to POST on every first snapshot regardless. With the server-side writer fixed, a
 * user whose claims are wrong repairs themselves here on their next sign-in, with no
 * script run at all.
 */
async function syncRoleClaimsForCurrentUser(documentRole?: string): Promise<void> {
  if (!auth?.currentUser) return;
  try {
    if (!(await claimsLookStale(auth.currentUser, documentRole))) return;
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${getApiBaseUrl()}/user/sync-claims`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      await auth.currentUser.getIdToken(true);
    }
  } catch {
    // Non-blocking; storage RBAC falls back until claims sync succeeds.
  }
}

export type ProfileError = 'permission-denied' | 'unavailable' | 'unknown' | null;

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  /**
   * Why the profile could not be read, when it could not be read.
   *
   * `profile === null && profileError === null` still means "this user has no
   * document". A non-null value means the read itself failed — which used to be
   * indistinguishable, so a permission-denied left the login screens silently
   * doing nothing and RoleGuard bouncing back to /login forever.
   */
  profileError: ProfileError;
  retryProfile: () => void;
  selectedRole: UserRole | null;
  setSelectedRole: (role: UserRole | null) => void;
  signup: (email: string, password: string, name: string, role?: UserRole) => Promise<any>;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: (
    role?: UserRole,
    options?: { isDemo?: boolean; allowProfileCreation?: boolean }
  ) => Promise<any>;
  loginAsGuestStudent: () => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | null>(null);
const ModalContext = createContext<ModalContextType>({
  activeModal: null,
  openModal: () => {},
  closeModal: () => {}
});

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
};

/**
 * Map a `users/{uid}` document onto UserProfile.
 *
 * This used to be written out three times — in fetchProfile, in the
 * onAuthStateChanged handler, and in the realtime listener — and the copies had
 * drifted: the listener's version omitted `partner_id`, so a partner's id was
 * present right after sign-in and then silently dropped the moment the snapshot
 * re-emitted. One mapping means that cannot happen again.
 *
 * `authFallback` supplies the Firebase Auth values used when the document has no
 * email/displayName of its own.
 */
function mapUserProfile(
  uid: string,
  data: Record<string, any>,
  authFallback: { email?: string | null; displayName?: string | null } = {}
): UserProfile {
  return {
    uid,
    email: data.email || authFallback.email || '',
    displayName: data.displayName || data.name || authFallback.displayName || '',
    name: data.name || data.displayName || authFallback.displayName || '',
    firstName: data.firstName,
    role: normalizeUserRole(data.role),
    approvalStatus: data.approvalStatus || null,
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt,
    age: data.age,
    class: data.class,
    curriculum: data.curriculum,
    school: data.school,
    onboardingCompleted: data.onboardingCompleted || false,
    onboardingCompletedAt: data.onboardingCompletedAt,
    userType: data.userType,
    teamSize: data.teamSize,
    usageType: data.usageType,
    newsletterSubscription: data.newsletterSubscription,
    // LMS-specific fields
    school_id: data.school_id,
    class_ids: data.class_ids,
    requested_class_id: data.requested_class_id ?? null,
    teacher_id: data.teacher_id,
    managed_class_ids: data.managed_class_ids,
    managed_school_id: data.managed_school_id,
    partner_id: data.partner_id,
    isGuest: data.isGuest || false,
    isDemo: data.isDemo || false,
    demoLocation: data.demoLocation,
  };
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileError, setProfileError] = useState<ProfileError>(null);
  // Bumped to re-subscribe the profile listener after a forced token refresh.
  const [retryKey, setRetryKey] = useState(0);
  // One automatic retry per uid. A ref, not state, so it cannot itself re-render.
  const retriedRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  // When opened from mobile app WebView with idToken in hash or query, sign in so Firestore/skybox/meshy work.
  // Skip on /studio-standalone and /vrplayer-standalone -- those pages handle auth themselves.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname.toLowerCase();
    if (path.includes('studio-standalone') || path.includes('vrplayer-standalone') || path.includes('main-standalone')) return;
    const hashMatch = window.location.hash.match(/[#&]idToken=([^&]+)/);
    const queryMatch = window.location.search.match(/[?&]idToken=([^&]+)/);
    const raw = hashMatch?.[1] || queryMatch?.[1];
    const idToken = raw ? decodeURIComponent(raw) : null;
    if (!idToken) return;
    (async () => {
      try {
        const base = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/auth/custom-token`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (res.ok) {
          const { customToken } = await res.json();
          if (customToken) {
            await signInWithCustomToken(auth, customToken);
            const search = window.location.search.replace(/[?&]idToken=[^&]+/g, '').replace(/^&/, '?').replace(/^\?$/, '');
            const hash = window.location.hash.replace(/[#&]idToken=[^&]+/g, '').replace(/^&/, '#').replace(/^#$/, '');
            window.history.replaceState(null, '', window.location.pathname + search + hash);
          }
        }
      } catch (e) {
        console.warn('WebView idToken sign-in failed:', e);
      }
    })();
  }, []);

  // Fetch user profile from Firestore
  const fetchProfile = useCallback(async (uid: string): Promise<UserProfile | null> => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return mapUserProfile(uid, userDoc.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Some authenticated browser sessions can reach Firebase Auth but have a
      // stale/denied Firestore transport. Fall back to the authenticated API;
      // the server reads only the caller's own UID and never accepts a UID from
      // the client, so this does not weaken authorization.
      try {
        const currentUser = auth?.currentUser;
        if (!currentUser || currentUser.uid !== uid) return null;
        const token = await currentUser.getIdToken();
        const response = await fetch(`${getApiBaseUrl()}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return null;
        const payload = await response.json();
        const data = payload?.data;
        return data ? mapUserProfile(uid, data, { email: currentUser.email, displayName: currentUser.displayName }) : null;
      } catch (fallbackError) {
        console.error('Error fetching profile from authenticated API:', fallbackError);
        return null;
      }
    }
  }, []);

  /**
   * Does this user already have a profile?
   *
   * `fetchProfile` answers null both for "no profile" and for "could not ask",
   * which is fine for rendering and dangerous for deciding whether to CREATE
   * one: acting on a failed read is how a sign-up payload gets written over an
   * account that already existed. Sign-in needs the three-way answer, so the
   * server — which reads the caller's own uid with the Admin SDK, and can reply
   * when the browser's Firestore transport cannot — is asked directly.
   */
  const resolveProfileExistence = useCallback(
    async (uid: string): Promise<{ status: 'found' | 'missing' | 'unknown'; data?: any }> => {
      try {
        const currentUser = auth?.currentUser;
        if (!currentUser || currentUser.uid !== uid) return { status: 'unknown' };
        const token = await currentUser.getIdToken();
        const response = await fetch(`${getApiBaseUrl()}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 404) return { status: 'missing' };
        if (!response.ok) return { status: 'unknown' };
        const payload = await response.json();
        return payload?.data ? { status: 'found', data: payload.data } : { status: 'missing' };
      } catch (error) {
        console.warn('[auth] Could not resolve profile existence via API:', error);
        return { status: 'unknown' };
      }
    },
    []
  );

  // Refresh profile manually
  const refreshProfile = useCallback(async () => {
    if (!user?.uid) return;
    setProfileLoading(true);
    const profileData = await fetchProfile(user.uid);
    setProfile(profileData);
    setProfileLoading(false);
  }, [user, fetchProfile]);

  // Manual retry for the ProfileUnavailable card: clear the once-per-uid guard and
  // re-subscribe, so a user who hit a transient denial can try again without
  // signing out.
  const retryProfile = useCallback(() => {
    retriedRef.current = null;
    setProfileError(null);
    setProfileLoading(true);
    setRetryKey((k) => k + 1);
  }, []);

  // Update user profile (guests may only complete onboarding once; no other writes)
  const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
    if (!user?.uid) {
      throw new Error('No user logged in');
    }
    // Allow guest to complete onboarding (one-time write with onboardingCompleted + isGuest)
    if (isGuestUser(profile) && !(data.onboardingCompleted === true && (data.isGuest === true || profile?.isGuest === true))) {
      throw new Error('Guest users cannot update profile. You are in read-only mode.');
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };
      
      await updateDoc(userDocRef, updateData);
      
      // Refresh local profile so redirect sees updated state
      await refreshProfile();
      
      return;
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  }, [user, profile, refreshProfile]);

  useEffect(() => {
    if (typeof window === 'undefined' || !auth) {
      console.warn('Auth: Not in browser environment or auth not available');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        if (firebaseUser) {
          setUser(firebaseUser);
          // The profile itself is delivered by the realtime listener below, which
          // fires with the document immediately. Fetching it here as well meant two
          // network reads of users/{uid} on every single page load for the same data.
          // profileLoading stays true until that first snapshot lands, so RoleGuard
          // and the login screens still wait for a profile exactly as before.
          setProfileLoading(true);
        } else {
          setUser(null);
          setProfile(null);
          setProfileError(null);
          retriedRef.current = null;
          resetClaimsRefresh();
          setProfileLoading(false);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Auth state change error:', error);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  // Sole source of profile state: one realtime listener on users/{uid}.
  // Keyed on user?.uid rather than the user object so a new FirebaseUser identity
  // for the same account does not tear the listener down and re-read the document.
  useEffect(() => {
    const uid = user?.uid;
    if (!uid) return;

    const authFallback = { email: user?.email, displayName: user?.displayName };
    let settled = false;

    // Clearing profileLoading is what releases the route guards, so it has to happen
    // on the first emission whatever that emission is — document, no document, or error.
    const settle = () => {
      if (settled) return;
      settled = true;
      setProfileLoading(false);
    };

    const userDocRef = doc(db, 'users', uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        setProfileError(null);
        if (snapshot.exists()) {
          /*
            Keep the previous object when nothing actually changed.

            Every snapshot used to produce a new profile identity, and a dozen
            effects across the dashboards depend on `profile` — so any write to
            the user document, including fields no screen displays, tore down
            and re-subscribed every dashboard query and put a full-page spinner
            over a page whose data was already correct.
          */
          const raw = snapshot.data();
          const nextProfile = mapUserProfile(uid, raw, authFallback);
          setProfile((current) => (sameProfile(current, nextProfile) ? current : nextProfile));
          // Deliberately read off the raw snapshot rather than the mapped profile:
          // putting claims_updated_at into UserProfile would give every claims write
          // a new profile identity and re-render every dashboard.
          if (user) void ensureFreshClaims(user, raw.claims_updated_at);
          if (!settled) void syncRoleClaimsForCurrentUser(nextProfile.role);
        } else {
          // New user — the profile is created during signup.
          setProfile(null);
        }
        settle();
      },
      async (error) => {
        console.error('Profile snapshot error:', error);
        const code = (error as { code?: string })?.code;

        // Recover from a Firestore-only permission/transport failure using the
        // authenticated own-profile endpoint before presenting a hard failure.
        if (user && (code === 'permission-denied' || code === 'unavailable')) {
          const fallbackProfile = await fetchProfile(uid);
          if (fallbackProfile) {
            setProfile(fallbackProfile);
            setProfileError(null);
            settle();
            return;
          }
        }

        // A denied read is very often a token that predates a claims change. Force
        // one refresh and re-subscribe before giving up — exactly once per uid, so
        // there is no storm to back off from.
        if (code === 'permission-denied' && user && retriedRef.current !== uid) {
          retriedRef.current = uid;
          try {
            await user.getIdToken(true);
            setRetryKey((k) => k + 1);
            return; // keep the existing profile; the re-subscribe settles it
          } catch (refreshError) {
            console.error('Token refresh after permission-denied failed:', refreshError);
          }
        }

        setProfileError(
          code === 'permission-denied'
            ? 'permission-denied'
            : code === 'unavailable'
              ? 'unavailable'
              : 'unknown'
        );
        setProfile(null);
        settle();
      }
    );

    // Deliberately does NOT settle: on an account switch this cleanup runs after
    // onAuthStateChanged has already set profileLoading back to true for the new
    // uid, so settling here would release the guards against a stale profile.
    // Sign-out is settled by the auth handler instead.
    return unsubscribe;
  }, [user?.uid, user?.email, user?.displayName, retryKey, fetchProfile]);

  const signup = async (email: string, password: string, name: string, requestedRole: UserRole = 'student') => {
    if (!auth) {
      throw new Error('Authentication service is not available');
    }
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Approval status set only after onboarding; until then null so they don't appear in approval queue
      const approvalStatus: ApprovalStatus = null;
      const now = new Date().toISOString();
      
      // Create user document with role in main users collection.
      // Signup.jsx passes the role the person picked; this used to be hardcoded to
      // 'student', so a teacher or school signup silently became a student and then
      // got the student branch of onboarding, with no way back. loginWithGoogle has
      // always honoured the selection — the two paths off one screen disagreed.
      const role: UserRole = resolveSignupRole(requestedRole);
      const userData = {
        name,
        displayName: name,
        email,
        role,
        approvalStatus,
        createdAt: now,
        updatedAt: now,
        onboardingCompleted: false,
        newsletterSubscription: true,
        userType: role,
      };
      
      await setDoc(doc(db, 'users', newUser.uid), userData);
      console.log('✅ Created user entry:', newUser.uid);
      
      // Create default subscription document
      await createDefaultSubscription(newUser.uid);
      
      // Set profile locally
      setProfile({
        uid: newUser.uid,
        ...userData,
      });
      
      toast.success('Account created successfully!');
      return newUser;
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error(error.message);
      throw error;
    }
  };

  const getLocationForDemo = (): Promise<{ latitude: number; longitude: number; timestamp: string } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          timestamp: new Date().toISOString(),
        }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });
  };

  const loginWithGoogle = async (
    role?: UserRole,
    options?: { isDemo?: boolean; allowProfileCreation?: boolean }
  ) => {
    if (!auth) {
      throw new Error('Authentication service is not available');
    }
    try {
      const provider = new GoogleAuthProvider();
      
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError: any) {
        console.warn('Popup failed, trying redirect:', popupError);
        
        if (popupError.code === 'auth/popup-closed-by-user' || 
            popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/cancelled-popup-request') {
          throw new Error('Please allow popups for this site and try again');
        }
        throw popupError;
      }
      
      const { user: googleUser } = result;
      const isDemo = options?.isDemo === true;
      
      const userDocRef = doc(db, 'users', googleUser.uid);

      /*
        Existing user or new one? Decide that before writing anything.

        A successful read settles it either way. A FAILED read settles nothing —
        and the old code treated it as "not new" by luck rather than design,
        while a read that wrongly reported absence would have created a profile
        over a real account. So a failed read asks the server instead, and a
        genuinely unknown answer creates nothing at all.
      */
      let existence: 'found' | 'missing' | 'unknown' = 'unknown';
      let existingProfileData: any = null;
      try {
        const snapshot = await getDoc(userDocRef);
        existence = snapshot.exists() ? 'found' : 'missing';
        existingProfileData = snapshot.exists() ? snapshot.data() : null;
      } catch (profileReadError) {
        // Do not turn a Firestore browser-read failure into a Google-auth
        // failure: the session is valid and the server can still answer.
        console.warn('Google sign-in succeeded; Firestore profile read deferred:', profileReadError);
        const resolved = await resolveProfileExistence(googleUser.uid);
        existence = resolved.status;
        existingProfileData = resolved.data ?? null;
      }

      /*
        An existing profile is published straight away, so the caller can route
        to the screen for this role instead of waiting on a listener that may be
        the very thing that just failed.
      */
      if (existence === 'found' && existingProfileData) {
        setProfile(mapUserProfile(googleUser.uid, existingProfileData, {
          email: googleUser.email,
          displayName: googleUser.displayName,
        }));
      }
      
      /*
        Diagnostics, deliberately limited to identity and outcome.

        Every failure below used to reach the user as one generic "Google login
        error", which is why a demoted profile and a missing one were
        indistinguishable. Never log tokens, passwords or OAuth codes.
      */
      console.info('[auth] Google sign-in resolved:', {
        projectId: (db as any)?.app?.options?.projectId,
        uid: googleUser.uid,
        email: googleUser.email,
        provider: result?.providerId ?? 'google.com',
        profilePath: `users/${googleUser.uid}`,
        profileExists: existence,
        profileRole: existingProfileData?.role,
      });

      /*
        Staff entrances pass allowProfileCreation: false.

        Auto-creating here is right for the public sign-up page and wrong
        everywhere else: from the staff door it manufactured a `student` for an
        account that simply has no profile yet, and the staff gate then logged
        that brand-new student straight back out.
      */
      if (existence === 'missing' && options?.allowProfileCreation === false) {
        const missing: Error & { code?: string; uid?: string } = new Error(
          'Google authentication succeeded, but no In3D.ai staff profile exists for this account. ' +
            `Contact an administrator with your Firebase UID: ${googleUser.uid}`
        );
        missing.code = 'learnxr/no-staff-profile';
        missing.uid = googleUser.uid;
        throw missing;
      }

      // Only a confirmed absence creates anything. 'unknown' falls through
      // untouched: the session is valid, and the listener or a retry will
      // resolve the profile without risking a write over a real account.
      if (existence === 'missing') {
        // New user - use selected role or default to student for demo
        const userRole = role || selectedRole || 'student';
        const approvalStatus: ApprovalStatus = null;
        const now = new Date().toISOString();
        
        const userData: Record<string, unknown> = {
          name: googleUser.displayName,
          displayName: googleUser.displayName,
          email: googleUser.email,
          role: userRole,
          approvalStatus,
          createdAt: now,
          updatedAt: now,
          onboardingCompleted: false,
          newsletterSubscription: true,
          userType: userRole,
          isDemo: isDemo || false,
        };
        
        /*
          merge, because this payload is a whole profile.

          A plain set() replaces the document. Reached with an existing profile
          — a read that raced, a retry, any future caller that trusts the check
          less than it should — it silently rewrites role, userType and
          onboardingCompleted to signup defaults. That is precisely how a live
          superadmin was demoted to `teacher` and locked out of the app.
        */
        await setDoc(userDocRef, userData, { merge: true });
        console.log('✅ Created user entry for Google user:', googleUser.uid);

        // A subscription is bookkeeping; failing to write one is not a failed
        // login, and reporting it as "Google login failed" sends people to
        // debug their Google account instead of the actual problem.
        try {
          await createDefaultSubscription(googleUser.uid);
        } catch (subscriptionError) {
          console.warn('Default subscription not created for new Google user:', subscriptionError);
        }

        setProfile({
          uid: googleUser.uid,
          ...userData,
        } as UserProfile);
      }
      
      if (isDemo) {
        // For both new and existing users: capture location and set isDemo
        try {
          const location = await getLocationForDemo();
          if (location) {
            await updateDoc(userDocRef, {
              isDemo: true,
              demoLocation: location,
              updatedAt: new Date().toISOString(),
            });
            const profileData = await fetchProfile(googleUser.uid);
            if (profileData) setProfile(profileData);
          } else {
            await updateDoc(userDocRef, {
              isDemo: true,
              updatedAt: new Date().toISOString(),
            });
            const profileData = await fetchProfile(googleUser.uid);
            if (profileData) setProfile(profileData);
          }
        } catch (locErr) {
          console.warn('Demo location capture failed:', locErr);
          await updateDoc(userDocRef, {
            isDemo: true,
            updatedAt: new Date().toISOString(),
          });
          const profileData = await fetchProfile(googleUser.uid);
          if (profileData) setProfile(profileData);
        }
      }
      
      setSelectedRole(null);
      
      toast.success(isDemo ? 'Demo started! Explore with Google sign-in.' : 'Logged in successfully with Google!');
      return googleUser;
    } catch (error: any) {
      console.error("Google login error:", error);
      
      /*
        Three different failures used to arrive as one message.

        "Google login failed" for an account that authenticated perfectly and
        merely has no profile sends people to re-check their Google account,
        which is the one thing that is working. Keep them apart.
      */
      let errorMessage = 'Login failed. Please try again.';
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Please allow popups for this site and try again.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Login was cancelled. Please try again.';
      } else if (error.code === 'learnxr/no-staff-profile') {
        // Authentication succeeded; there is simply no profile to authorise.
        errorMessage = error.message;
      } else if (typeof error.code === 'string' && error.code.startsWith('auth/')) {
        errorMessage = `Google authentication failed: ${error.message || error.code}`;
      } else if (error.code === 'permission-denied') {
        errorMessage =
          'Google authentication succeeded, but your profile could not be loaded. Please try again or contact an administrator.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  const loginAsGuestStudent = async () => {
    if (!auth || !db) {
      throw new Error('Authentication service is not available');
    }
    try {
      const { user: guestUser } = await signInAnonymously(auth);
      const userDocRef = doc(db, 'users', guestUser.uid);
      const userDoc = await getDoc(userDocRef);
      const now = new Date().toISOString();
      if (!userDoc.exists()) {
        const userData = {
          email: guestUser.email || '',
          displayName: 'Guest Explorer',
          name: 'Guest Explorer',
          role: 'student' as UserRole,
          approvalStatus: null as ApprovalStatus,
          onboardingCompleted: false,
          isGuest: true,
          createdAt: now,
          updatedAt: now,
        };
        await setDoc(userDocRef, userData);
        const profileData: UserProfile = {
          uid: guestUser.uid,
          ...userData,
          createdAt: userData.createdAt,
        };
        setProfile(profileData);
      } else {
        const profileData = await fetchProfile(guestUser.uid);
        setProfile(profileData);
      }
      setSelectedRole(null);
      toast.success('Exploring as guest! Complete the quick setup to continue.');
      return guestUser;
    } catch (error: any) {
      console.error('Guest login error:', error);
      toast.error(error?.message || 'Could not start guest session');
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    if (!auth) {
      throw new Error('Authentication service is not available');
    }
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      /*
        Heal a signup that only half-finished.

        createUserWithEmailAndPassword and the setDoc that follows it are two
        separate awaits with no transaction between them, so a failed write — or a
        tab closed in that window — leaves an Auth account with no user document.
        The account then authenticates fine and nothing else works, permanently:
        three such accounts exist in production, spread across June, July and
        September, so this is rare but ongoing rather than historical.

        loginWithGoogle has always created the document when it is missing. Doing
        the same here costs one read per login and needs no repair script. The role
        is deliberately the least-privileged one: what they originally chose was
        never recorded anywhere, so onboarding asks again.
      */
      const healRef = doc(db, 'users', result.user.uid);
      if (!(await getDoc(healRef)).exists()) {
        const now = new Date().toISOString();
        const role = resolveSignupRole(null);
        await setDoc(healRef, {
          name: result.user.displayName || '',
          displayName: result.user.displayName || '',
          email: result.user.email || email,
          role,
          approvalStatus: null as ApprovalStatus,
          createdAt: now,
          updatedAt: now,
          onboardingCompleted: false,
          newsletterSubscription: true,
          userType: role,
        });
        console.log('Recreated missing user document on login:', result.user.uid);
      }

      toast.success('Logged in successfully!');
      return result;
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message);
      throw error;
    }
  };

  const logout = async () => {
    if (!auth) {
      throw new Error('Authentication service is not available');
    }
    try {
      // Release the generation-job listeners before the credential goes away.
      // The service is a module-level singleton, so without this the next user in
      // the same tab would inherit listeners on the previous user's jobs.
      try {
        const { backgroundGenerationService } = await import('../services/backgroundGenerationService');
        backgroundGenerationService.cleanup();
      } catch (cleanupError) {
        console.warn('Background generation cleanup failed during logout:', cleanupError);
      }

      await signOut(auth);
      setProfile(null);
      setSelectedRole(null);
      toast.success('Logged out successfully!');
      return Promise.resolve();
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message);
      return Promise.reject(error);
    }
  };

  const resetPassword = async (email: string) => {
    if (!auth) {
      throw new Error('Authentication service is not available');
    }
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast.error(error.message);
      throw error;
    }
  };

  /*
    Memoised so a render of this provider does not re-render every consumer of
    useAuth. An inline object literal here changed identity on every render,
    which put avoidable work through every screen in the app on any auth state
    change at all.
  */
  const authValue = useMemo(
    () => ({
      user,
      profile,
      selectedRole,
      setSelectedRole,
      signup,
      login,
      loginWithGoogle,
      loginAsGuestStudent,
      logout,
      resetPassword,
      updateProfile,
      refreshProfile,
      retryProfile,
      loading,
      profileLoading,
      profileError,
    }),
    [
      user,
      profile,
      selectedRole,
      signup,
      login,
      loginWithGoogle,
      loginAsGuestStudent,
      logout,
      resetPassword,
      updateProfile,
      refreshProfile,
      retryProfile,
      loading,
      profileLoading,
      profileError,
    ]
  );

  const modalContextValue: ModalContextType = {
    activeModal,
    openModal: (modalType) => setActiveModal(modalType),
    closeModal: () => setActiveModal(null)
  };

  return (
    <AuthContext.Provider value={authValue}>
      <ModalContext.Provider value={modalContextValue}>
        {!loading && children}
      </ModalContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
